import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const caseStudies = [
  {
    n: '01',
    title: 'Commercial Security Company',
    tag: 'Security & Surveillance',
    desc: 'Supported outbound sales and appointment setting for a commercial security provider targeting property managers, commercial landlords, and business owners across multiple regions.',
    sections: [
      { label: 'Type of Outbound Work', text: 'Cold calling and follow-up cadences targeting commercial decision-makers. Focused on generating qualified discovery calls for the internal sales team.' },
      { label: 'Business Development Support', text: 'Helped identify and segment target verticals including multi-unit properties, retail locations, and office buildings.' },
      { label: 'Appointment Setting', text: 'Consistent pipeline of booked appointments with property managers, operations leads, and business owners.' },
      { label: 'CRM & Follow-Up Systems', text: 'Managed lead tracking, follow-up scheduling, and pipeline visibility. Ensured no opportunity fell through the cracks.' },
      { label: 'Decision-Maker Outreach', text: 'Direct outreach to facility managers, directors of operations, and ownership-level contacts.' }
    ]
  },
  {
    n: '02',
    title: 'Robotics & Automation Company',
    tag: 'Robotics & Industrial Automation',
    desc: 'Contributed to business development efforts for a company offering robotic and automation solutions to commercial and industrial clients. Involved in both outreach strategy and operational exposure.',
    sections: [
      { label: 'Type of Outbound Work', text: 'Targeted outreach to warehouse operators, manufacturing facilities, and logistics companies evaluating automation solutions.' },
      { label: 'Business Development Support', text: 'Assisted in identifying prospects, preparing outreach materials, and supporting the sales process from initial contact through to qualified opportunity.' },
      { label: 'Operational Exposure', text: 'Gained direct exposure to how automation solutions are positioned, demonstrated, and sold to industrial buyers.' },
      { label: 'Decision-Maker Outreach', text: 'Outreach focused on operations directors, plant managers, and procurement leads within mid-market and enterprise organizations.' }
    ]
  },
  {
    n: '03',
    title: 'Energy & Solar Campaign',
    tag: 'Energy / Renewable Energy',
    desc: 'Ran outbound calling campaigns for a solar and energy services company. Handled a high volume of outreach to residential and commercial prospects, managing follow-up and appointment pipelines.',
    sections: [
      { label: 'Type of Outbound Work', text: 'High-volume cold calling combined with structured follow-up sequences. Both residential and commercial channels.' },
      { label: 'Lead Generation', text: 'Generated qualified interest from homeowners and commercial building owners exploring energy cost reduction options.' },
      { label: 'Appointment Setting', text: 'Booked consultations for energy advisors, maintaining consistent daily and weekly appointment volume.' },
      { label: 'CRM & Follow-Up Systems', text: 'Managed pipeline through structured follow-up protocols, ensuring warm leads were re-engaged at the right intervals.' }
    ]
  },
  {
    n: '04',
    title: 'Commercial Services Business',
    tag: 'Commercial Facilities & Maintenance',
    desc: 'Provided outbound support and business development assistance for a company offering commercial services to office buildings, retail environments, and managed properties.',
    sections: [
      { label: 'Type of Outbound Work', text: 'Direct outreach to property managers, facility directors, and operations contacts across commercial real estate portfolios.' },
      { label: 'Business Development Support', text: 'Helped build and work through prospect lists, identify decision-makers, and establish initial conversations with new accounts.' },
      { label: 'Lead Generation', text: 'Focused on generating a consistent flow of qualified conversations for the internal team to convert.' },
      { label: 'Decision-Maker Outreach', text: 'Navigated multi-layered organizations to reach the individuals responsible for vendor selection and service contracts.' }
    ]
  },
  {
    n: '05',
    title: 'Marketing & Media Campaigns',
    tag: 'Marketing & Media',
    desc: 'Worked alongside marketing and media organizations on client acquisition and business development initiatives. Gained exposure to agency-side sales and the full client lifecycle.',
    sections: [
      { label: 'Type of Outbound Work', text: 'Prospecting for new agency clients, outreach to business owners and marketing managers exploring advertising and content solutions.' },
      { label: 'Business Development Support', text: 'Supported new business development including outreach, follow-up, and proposal pipeline management.' },
      { label: 'CRM & Follow-Up Systems', text: 'Maintained organized contact and opportunity records, ensuring consistent follow-through on every active conversation.' },
      { label: 'Decision-Maker Outreach', text: 'Engaged owners, CMOs, and marketing directors at SMBs and mid-market companies.' }
    ]
  },
  {
    n: '06',
    title: 'Home Services Outreach',
    tag: 'Home Services & Residential',
    desc: 'Conducted outbound campaigns for home services providers, including appointment setting, lead reactivation, and follow-up management for residential service businesses.',
    sections: [
      { label: 'Type of Outbound Work', text: 'Outbound calling and follow-up for residential leads — both new prospects and dormant contacts who had previously shown interest.' },
      { label: 'Appointment Setting', text: 'Consistently booked in-home or phone consultations for service teams, maintaining predictable appointment volume.' },
      { label: 'Lead Generation & Reactivation', text: 'Re-engaged older leads in the CRM that had gone cold, converting them back into active opportunities.' },
      { label: 'CRM & Follow-Up Systems', text: 'Set up and maintained follow-up workflows to ensure leads were contacted at the right time with the right message.' }
    ]
  }
];

const industries = [
  'Security',
  'Robotics & Automation',
  'Energy & Solar',
  'SaaS & Software',
  'Marketing & Media',
  'Commercial Services',
  'Home Services',
  'Construction-Related',
  'Office & Workplace Solutions',
  'Facilities Management'
];

export default function CaseStudies() {
  return (
    <div className="page">

      <section style={{ paddingBottom: 64, borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="red-line" />
          <span className="eyebrow">Experience</span>
          <h1 style={{ fontSize: 'clamp(40px, 5vw, 72px)', maxWidth: 760 }}>
            Real-World Outbound &<br />
            <span className="h1-dim">Business Development Experience.</span>
          </h1>
          <p className="lead" style={{ marginTop: 24, maxWidth: 720 }}>
            Before founding Zahela, Robbie worked hands-on across multiple industries — running outbound campaigns, setting appointments, managing follow-up systems, and supporting business development from the ground up. The work below reflects real campaign experience across real businesses. Company names are withheld by design.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section>
        <div className="container">
          {caseStudies.map((c, i) => (
            <div key={i} style={{
              padding: '64px 0',
              borderBottom: '1px solid var(--border)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 32, alignItems: 'start' }}>
                <div className="process-number">{c.n}</div>
                <div>
                  <span className="case-tag">{c.tag}</span>
                  <h2 style={{ fontSize: 'clamp(24px, 2.5vw, 34px)', marginBottom: 16 }}>{c.title}</h2>
                  <p style={{ color: 'var(--gray-mid)', lineHeight: 1.85, fontSize: 15, maxWidth: 760, marginBottom: 40 }}>{c.desc}</p>

                  <div className="grid-2" style={{ border: '1px solid var(--border)' }}>
                    {c.sections.map((s, j) => (
                      <div key={j} style={{ background: 'var(--black-card)', padding: '28px 32px' }}>
                        <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--red)', marginBottom: 12 }}>{s.label}</p>
                        <p style={{ fontSize: 14, color: 'var(--gray-light)', lineHeight: 1.75 }}>{s.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Industry Experience */}
      <section style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ marginBottom: 40 }}>
            <div className="red-line" />
            <span className="eyebrow">Breadth of Experience</span>
            <h2>Industry Experience Includes</h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {industries.map((ind, i) => (
              <span key={i} style={{
                fontSize: 12,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--gray-light)',
                border: '1px solid var(--border)',
                padding: '10px 18px'
              }}>
                {ind}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--red)', padding: '80px 0' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 32 }}>
          <div>
            <h2 style={{ color: 'var(--white)', marginBottom: 12 }}>Looking to build a stronger outbound system or reactivate your pipeline?</h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15 }}>Let's talk. We'll figure out where your revenue opportunities are and how to go get them.</p>
          </div>
          <Link to="/contact" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'var(--white)', flexShrink: 0 }}>
            Book a Strategy Call <ArrowRight size={14} />
          </Link>
        </div>
      </section>

    </div>
  );
}
